import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  TextField,
  Container,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import { useParams, Link } from "react-router-dom";
import { useNotification } from "../../context/notification.context";
import AddIcon from "@mui/icons-material/Add";
import { TableComponent } from "../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import { budget } from "../../api/budget";
import { providers } from "../../api/providers";
import { payments } from "../../api/pays";
import { ModalPaymentProductComponent } from "../../components";
import { ModalInvoiceComponent } from "../../components";
import { format } from "date-fns";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import ArticleIcon from "@mui/icons-material/Article";

export const ProductPage: React.FC<{}> = () => {
  const { id } = useParams();
  const { getError, getSuccess } = useNotification();
  const [product, setProduct] = useState<any>(null);
  const [pagos, setPagos] = useState<any[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isModalInvoiceOpen, setIsModalInvoiceOpen] = useState(false);
  const [isModalPaymentOpen, setIsModalPaymentOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const isDB2 = /^[hls]_\d+$/.test(id as string);
        const response = isDB2
          ? await providers.getItemById(id as string)
          : await budget.getById(id as any);

        const data = isDB2 ? response.data : response.data;

        // Normaliza moneda de DB2 ("USD"→"U$S", "ARS"→"$")
        const monedaNorm = (m: string) => m === "USD" ? "U$S" : m === "ARS" ? "$" : m;

        setProduct({
          id: data.id,
          proveedor: data.proveedore?.nombre,
          proveedor_id: data.proveedore?.id,
          tipo: isDB2
            ? data.tipo
            : (data.hotele_id ? "Alojamiento" : data.linea_id ? "Aéreo" : "Servicio"),
          file: data.file,
          producto: data.presupuesto?.id,
          cliente: data.presupuesto?.nombre_cliente,
          fecha_viaje: (data.fecha_salida !== null)
            ? format(new Date(data.fecha_salida), "dd/MM/yyyy")
            : format(new Date(data.presupuesto?.fecha_salida), "dd/MM/yyyy"),
          concepto: data.presupuesto?.concepto,
          descripcion: data.presupuesto?.descripcion,
          moneda: monedaNorm(data.moneda ?? "$"),
          importe: formatNumber(data.costo ?? 0),
          pagado: formatNumber(data.totalPaid ?? 0),
          saldo: formatNumber(data.saldo ?? 0),
          facturas: data.facturas
        });

        const pagosList = (data.pagos ?? []);
        pagosList.forEach((payment: any) => {
          const moneda = monedaNorm(payment.moneda ?? "$");
          payment.fecha = new Date(payment.createdAt ?? payment.created_at);
          payment.importeLit = payment.importe;
          payment.importe = moneda + " " + formatNumber(payment.importe);
          payment.valorDeCambioLit = payment.valor_de_cambio ?? payment.valorDeCambio;
          payment.valorDeCambio = (moneda !== "$")
            ? "$ " + formatNumber(payment.valor_de_cambio ?? payment.valorDeCambio ?? 0)
            : "-";
        });
        setPagos(pagosList);
      } catch (error) {
        getError(
          `Ocurrió un error inesperado al cargar el producto: ${error}`
        );
      }
    };
    fetchData();
  }, [getError, id, getSuccess]);

  // Function to format numbers with custom separators
  const formatNumber = (number: any) => {
    return number.toLocaleString('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
      groupingSeparator: '.',
      decimalSeparator: ',',
    });
  };

  if (!product || isLoading) {
    return <CircularProgress sx={{
      position: 'absolute',
      top: '50%',
      left: '50%'}}/>;
  }

  const handleModalInvoiceOpen = () => {
    setIsModalInvoiceOpen(true);
  };

  const handleModalInvoiceClose = () => {
    setIsModalInvoiceOpen(false);
  };

  const handleModalPaymentOpen = () => {
    setIsModalPaymentOpen(true);
  };

  const handleModalPaymentClose = () => {
    setSelectedPayment({});
    setIsModalPaymentOpen(false);
  };

  const handleDeletePayment = async (id: number) => {
    try {
      setIsLoading(true);
      await payments.delete(id);   
      getSuccess("Pago eliminado correctamente");  
      setIsLoading(false);
      setDialogOpen(false);
    } catch (error) {
      getError(`Error al eliminar el elemento: ${error}`);
    }
  };

  const handleEditModalOpen = (payment: any) => {    
    setSelectedPayment(payment);
    setIsModalPaymentOpen(true);
  };

  const handleDialogOpen = (id: number) => {
    setIdToDelete(id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const columns: GridColDef[] = [    
    {
      field: "fecha",
      headerName: "Fecha Pago",
      width: 250,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      type: "date",
      valueGetter: ({ value }) => value,
    },
    {
      field: "importe",
      headerName: "Importe",
      width: 250,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    },
    {
      field: "valorDeCambio",
      headerName: "Tipo de Cambio",
      width: 250,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    },
    {
      field: "observaciones",
      headerName: "Observaciones",
      type: "number",
      width: 350,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    }    
  ];

  return (
    <div>
      <Container maxWidth="xl" sx={{ pt: 5}}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          <IconButton aria-label="routeback" size="small" component={Link} to={"/proveedor/"+product.proveedor_id}>
            <ArrowBackIos fontSize="small" />
          </IconButton>        
          Información del Producto
        </Typography>
        
        <Grid container sx={{ mb: 3 }}>          
          <Grid item xs={3} sx={{ pr: 3 }}>
            <TextField
              label="Proveedor"
              key="proveedor"
              value={product.proveedor}
              disabled={true}
              fullWidth
            />
          </Grid>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Tipo Producto"
              key="tipo"
              value={product.tipo}
              disabled={true}
              fullWidth
            />
          </Grid>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="File"
              key="file"
              value={product.file}
              disabled={true}
              fullWidth
            />
          </Grid>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Producto"
              key="producto"
              value={product.producto}
              disabled={true}
              fullWidth
            />  
          </Grid> 
          <Grid item xs={3}>
            <TextField
              label="Cliente"
              key="cliente"
              value={product.cliente}
              disabled={true}
              fullWidth
            />     
          </Grid>          
        </Grid>
        <Grid container sx={{ mb: 3 }}>  
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Fecha Viaje"
              key="fecha_viaje"
              value={product.fecha_viaje}
              disabled={true}
              fullWidth
            />  
          </Grid>         
          <Grid item xs={5} sx={{ pr: 3 }}>
            <TextField
              label="Concepto"
              key="concepto"
              value={product.concepto}
              disabled={true}
              fullWidth
            /> 
          </Grid>    
          <Grid item xs={5}>
            <TextField
              label="Descripción"
              key="descripcion"
              value={product.descripcion}
              disabled={true}
              fullWidth
            />
          </Grid>                
        </Grid>
        <Grid container sx={{ mb: 3 }}>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Importe Costo"
              key="importe"
              value={product.moneda + " " + product.importe}
              disabled={true}
              fullWidth
            /> 
          </Grid>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Pagado"
              key="pagado"
              value={product.moneda + " " + product.pagado}
              disabled={true}
              fullWidth
            /> 
          </Grid>
          <Grid item xs={2} sx={{ pr: 3 }}>
            <TextField
              label="Saldo"
              key="saldo"
              value={product.moneda + " " + product.saldo}
              disabled={true}
              fullWidth
            /> 
          </Grid>
          <Grid item xs={5} sx={{ pr: 3 }}>
            <TextField
              label="Facturas"
              key="facturas"
              value={product.facturas}
              disabled={true}
              fullWidth
            /> 
          </Grid>
          <Grid item xs={1}>
            <IconButton aria-label="edit" size="medium" sx={{ mt: 1 }} onClick={() => handleModalInvoiceOpen()} >
              <ArticleIcon fontSize="medium" />
            </IconButton>
          </Grid>
        </Grid>
      </Container>
      <Paper variant="outlined" />
      <Container maxWidth="xl">
        <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
          Pagos Asociados
        </Typography>

        <Button
          variant="contained"
          color="primary"
          sx={{ mb: 2, mt: 2 }}
          startIcon={<AddIcon />}
          onClick={handleModalPaymentOpen}
          style={{float: "right"}}
        >
          Agregar pago
        </Button>

        {payments ? (
          <TableComponent
            columnData={columns}
            rowData={pagos}
            actions={true}
            actionsType={"payments"}
            onEdit={handleEditModalOpen}
            onDelete={handleDialogOpen}
            exportTitle={"Listado de Pagos - File " + product.file+ " - " + product.proveedor}
          />
        ) : (
          <Typography variant="body1">Cargando pagos...</Typography>
        )}        
    
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Eliminar Pago
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              ¿Está seguro que desea eliminar el pago?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancelar</Button>
            <Button onClick={() => {handleDeletePayment((idToDelete != null)?idToDelete:0)}} autoFocus>
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        <ModalInvoiceComponent
          open={isModalInvoiceOpen}
          onClose={handleModalInvoiceClose}
          title={"Facturas Asociadas | File " + product.file}
          productId={id}
          initialData={product}
        />

        <ModalPaymentProductComponent
          open={isModalPaymentOpen}
          onClose={handleModalPaymentClose}
          title={(selectedPayment && Object.keys(selectedPayment).length > 0) ? "Editar Pago | " + product.proveedor + " | File " + product.file : "Agregar Pago | " + product.proveedor + " | File " + product.file}
          providerID={product.proveedor_id}
          productType={product.tipo}
          productID={product.id}
          file={product.file}
          cliente={product.cliente}
          initialData={selectedPayment}
        />

      </Container>
    </div>
  );
};
